/**
 * Tracking Logs Component
 * Displays user activity logs for superadmin monitoring
 * Multi-tenant with full access for superadmin
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity,
  Search,
  Filter,
  RefreshCw,
  Calendar,
  User,
  Monitor,
  Globe,
  Clock,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Download,
  Trash2,
  Eye,
  X,
  MousePointer,
  FileText,
  Settings,
  Target,
  Shield,
  BarChart3,
  TrendingUp,
  Users,
  Loader2
} from 'lucide-react';
import { historyActions } from '../services/api/client';
import { toast } from '../utils/toastUtils';

// Action type icons
const ACTION_ICONS = {
  page_view: Monitor,
  click: MousePointer,
  button_click: MousePointer,
  form_submit: FileText,
  scan_start: Target,
  scan_stop: Target,
  scan_completed: Target,
  search: Search,
  filter: Filter,
  export: Download,
  download: Download,
  login: Shield,
  logout: Shield,
  setting_change: Settings,
  api_call: Globe,
  error: AlertCircle,
  default: Activity
};

// Action category colors
const CATEGORY_COLORS = {
  navigation: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500' },
  scan: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500' },
  exploitation: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500' },
  settings: { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500' },
  auth: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500' },
  report: { bg: 'bg-cyan-500/20', text: 'text-cyan-400', border: 'border-cyan-500' },
  data: { bg: 'bg-gray-500/20', text: 'text-gray-400', border: 'border-gray-500' },
  api: { bg: 'bg-indigo-500/20', text: 'text-indigo-400', border: 'border-indigo-500' },
  default: { bg: 'bg-gray-500/20', text: 'text-gray-400', border: 'border-gray-500' }
};

const TrackingLogs = () => {
  // State
  const [actions, setActions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(50);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [actionTypeFilter, setActionTypeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [pageFilter, setPageFilter] = useState('');
  const [errorOnly, setErrorOnly] = useState(false);
  const [dateRange, setDateRange] = useState(7); // days
  const [showFilters, setShowFilters] = useState(false);

  // Detail modal
  const [selectedAction, setSelectedAction] = useState(null);

  // Fetch actions
  const fetchActions = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        error_only: errorOnly
      };

      if (actionTypeFilter) params.action_type = actionTypeFilter;
      if (categoryFilter) params.action_category = categoryFilter;
      if (pageFilter) params.page_filter = pageFilter;

      // Calculate date range
      if (dateRange > 0) {
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - dateRange);
        params.start_date = startDate.toISOString();
      }

      const response = await historyActions.getActions(params);
      setActions(response.actions || []);
      setTotal(response.total || 0);
      setTotalPages(response.pages || 1);
    } catch (error) {
      console.error('Error fetching actions:', error);
      toast.error('Erreur lors du chargement des logs');
    } finally {
      setLoading(false);
    }
  }, [page, limit, actionTypeFilter, categoryFilter, pageFilter, errorOnly, dateRange]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const response = await historyActions.getStats(dateRange);
      setStats(response);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, [dateRange]);

  // Initial load
  useEffect(() => {
    fetchActions();
    fetchStats();
  }, [fetchActions, fetchStats]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [actionTypeFilter, categoryFilter, pageFilter, errorOnly, dateRange]);

  // Handle cleanup
  const handleCleanup = async (days) => {
    if (!window.confirm(`Supprimer tous les logs de plus de ${days} jours ?`)) return;

    try {
      const response = await historyActions.cleanup(days);
      toast.success(`${response.deleted} logs supprimes`);
      fetchActions();
      fetchStats();
    } catch (error) {
      toast.error('Erreur lors du nettoyage');
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Get icon for action type
  const getActionIcon = (actionType) => {
    const Icon = ACTION_ICONS[actionType] || ACTION_ICONS.default;
    return Icon;
  };

  // Get colors for category
  const getCategoryColors = (category) => {
    return CATEGORY_COLORS[category] || CATEGORY_COLORS.default;
  };

  // Action detail modal
  const ActionDetailModal = () => {
    if (!selectedAction) return null;

    const colors = getCategoryColors(selectedAction.action_category);
    const Icon = getActionIcon(selectedAction.action_type);

    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
        <div className="bg-brand-black-light border border-gray-700 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${colors.bg}`}>
                <Icon className={`w-5 h-5 ${colors.text}`} />
              </div>
              <div>
                <h3 className="text-white font-medium">{selectedAction.action_type}</h3>
                <p className="text-sm text-gray-400">{formatDate(selectedAction.created_at)}</p>
              </div>
            </div>
            <button
              onClick={() => setSelectedAction(null)}
              className="text-gray-400 hover:text-white p-2"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-brand-black rounded-lg p-3">
                <div className="text-xs text-gray-500 mb-1">Utilisateur</div>
                <div className="text-white flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  {selectedAction.user_id || 'Anonyme'}
                </div>
              </div>
              <div className="bg-brand-black rounded-lg p-3">
                <div className="text-xs text-gray-500 mb-1">Categorie</div>
                <div className={`${colors.text} flex items-center gap-2`}>
                  {selectedAction.action_category || '-'}
                </div>
              </div>
            </div>

            {/* Page Info */}
            {selectedAction.page && (
              <div className="bg-brand-black rounded-lg p-3">
                <div className="text-xs text-gray-500 mb-1">Page</div>
                <div className="text-white">{selectedAction.page}</div>
                {selectedAction.page_title && (
                  <div className="text-sm text-gray-400">{selectedAction.page_title}</div>
                )}
              </div>
            )}

            {/* Resource Info */}
            {selectedAction.resource_type && (
              <div className="bg-brand-black rounded-lg p-3">
                <div className="text-xs text-gray-500 mb-1">Ressource</div>
                <div className="text-white">
                  {selectedAction.resource_type}: {selectedAction.resource_name || selectedAction.resource_id}
                </div>
              </div>
            )}

            {/* API Info */}
            {selectedAction.endpoint && (
              <div className="bg-brand-black rounded-lg p-3">
                <div className="text-xs text-gray-500 mb-1">Endpoint API</div>
                <div className="text-white font-mono text-sm">
                  <span className="text-brand-green">{selectedAction.method}</span> {selectedAction.endpoint}
                </div>
                {selectedAction.status_code && (
                  <div className={`text-sm mt-1 ${
                    selectedAction.status_code.startsWith('2') ? 'text-green-400' :
                    selectedAction.status_code.startsWith('4') ? 'text-yellow-400' :
                    selectedAction.status_code.startsWith('5') ? 'text-red-400' : 'text-gray-400'
                  }`}>
                    Status: {selectedAction.status_code}
                  </div>
                )}
                {selectedAction.duration_ms && (
                  <div className="text-sm text-gray-400">Duree: {selectedAction.duration_ms}ms</div>
                )}
              </div>
            )}

            {/* Client Info */}
            <div className="bg-brand-black rounded-lg p-3">
              <div className="text-xs text-gray-500 mb-2">Informations Client</div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-500">IP :</span>{' '}
                  <span className="text-white">{selectedAction.ip_address || '-'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Navigateur:</span>{' '}
                  <span className="text-white">{selectedAction.browser || '-'}</span>
                </div>
                <div>
                  <span className="text-gray-500">OS:</span>{' '}
                  <span className="text-white">{selectedAction.os || '-'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Device:</span>{' '}
                  <span className="text-white">{selectedAction.device_type || '-'}</span>
                </div>
              </div>
            </div>

            {/* Description */}
            {selectedAction.description && (
              <div className="bg-brand-black rounded-lg p-3">
                <div className="text-xs text-gray-500 mb-1">Description</div>
                <div className="text-white">{selectedAction.description}</div>
              </div>
            )}

            {/* Error Info */}
            {selectedAction.error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                <div className="text-xs text-red-400 mb-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> Erreur
                </div>
                <div className="text-red-300">{selectedAction.error_message || 'Erreur inconnue'}</div>
              </div>
            )}

            {/* Details JSON */}
            {selectedAction.details && Object.keys(selectedAction.details).length > 0 && (
              <div className="bg-brand-black rounded-lg p-3">
                <div className="text-xs text-gray-500 mb-1">Details</div>
                <pre className="text-xs text-gray-300 overflow-x-auto">
                  {JSON.stringify(selectedAction.details, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Activity className="w-6 h-6 text-brand-green" />
          <h2 className="text-xl font-bold text-white">Tracking Logs</h2>
          <span className="px-2 py-1 bg-brand-green/20 text-brand-green text-xs rounded">
            Super Admin
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { fetchActions(); fetchStats(); }}
            disabled={loading}
            className="p-2 text-gray-400 hover:text-brand-green rounded-lg hover:bg-brand-black"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => handleCleanup(90)}
            className="px-3 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Nettoyer (+90j)
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-brand-black-light rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-white">
                {statsLoading ? '-' : stats?.total_actions?.toLocaleString() || 0}
              </div>
              <div className="text-sm text-gray-400">Actions totales</div>
            </div>
            <BarChart3 className="w-8 h-8 text-brand-green opacity-50" />
          </div>
        </div>

        <div className="bg-brand-black-light rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-white">
                {statsLoading ? '-' : stats?.unique_users || 0}
              </div>
              <div className="text-sm text-gray-400">Utilisateurs actifs</div>
            </div>
            <Users className="w-8 h-8 text-blue-400 opacity-50" />
          </div>
        </div>

        <div className="bg-brand-black-light rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-white">
                {statsLoading ? '-' : Object.keys(stats?.top_pages || {}).length}
              </div>
              <div className="text-sm text-gray-400">Pages visitees</div>
            </div>
            <Monitor className="w-8 h-8 text-purple-400 opacity-50" />
          </div>
        </div>

        <div className="bg-brand-black-light rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-red-400">
                {statsLoading ? '-' : stats?.errors || 0}
              </div>
              <div className="text-sm text-gray-400">Erreurs</div>
            </div>
            <AlertCircle className="w-8 h-8 text-red-400 opacity-50" />
          </div>
        </div>
      </div>

      {/* Top Pages & Action Types */}
      {stats && (
        <div className="grid grid-cols-2 gap-4">
          {/* Top Pages */}
          <div className="bg-brand-black-light rounded-lg p-4 border border-gray-700">
            <h3 className="text-white font-medium mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-brand-green" />
              Pages les plus visitees
            </h3>
            <div className="space-y-2">
              {Object.entries(stats.top_pages || {}).slice(0, 5).map(([pagePath, count]) => (
                <div key={pagePath} className="flex items-center justify-between text-sm">
                  <span className="text-gray-400 truncate">{pagePath}</span>
                  <span className="text-white font-medium">{count}</span>
                </div>
              ))}
              {Object.keys(stats.top_pages || {}).length === 0 && (
                <div className="text-gray-500 text-sm">Aucune donnee</div>
              )}
            </div>
          </div>

          {/* Action Types */}
          <div className="bg-brand-black-light rounded-lg p-4 border border-gray-700">
            <h3 className="text-white font-medium mb-3 flex items-center gap-2">
              <Activity className="w-4 h-4 text-brand-green" />
              Types d'actions
            </h3>
            <div className="space-y-2">
              {Object.entries(stats.by_type || {}).slice(0, 5).map(([type, count]) => {
                const Icon = getActionIcon(type);
                return (
                  <div key={type} className="flex items-center justify-between text-sm">
                    <span className="text-gray-400 flex items-center gap-2">
                      <Icon className="w-3 h-3" />
                      {type}
                    </span>
                    <span className="text-white font-medium">{count}</span>
                  </div>
                );
              })}
              {Object.keys(stats.by_type || {}).length === 0 && (
                <div className="text-gray-500 text-sm">Aucune donnee</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-brand-black-light rounded-lg border border-gray-700 p-4">
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Rechercher…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-brand-green focus:outline-none text-sm"
            />
          </div>

          {/* Date Range */}
          <select
            value={dateRange}
            onChange={(e) => setDateRange(Number(e.target.value))}
            className="px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:border-brand-green focus:outline-none"
          >
            <option value={1}>24h</option>
            <option value={7}>7 jours</option>
            <option value={30}>30 jours</option>
            <option value={90}>90 jours</option>
          </select>

          {/* Filter toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-lg border ${
              showFilters ? 'border-brand-green bg-brand-green/10 text-brand-green' : 'border-gray-700 text-gray-400'
            }`}
          >
            <Filter className="w-5 h-5" />
          </button>

          {/* Error only toggle */}
          <button
            onClick={() => setErrorOnly(!errorOnly)}
            className={`px-3 py-2 rounded-lg border text-sm flex items-center gap-2 ${
              errorOnly ? 'border-red-500 bg-red-500/10 text-red-400' : 'border-gray-700 text-gray-400'
            }`}
          >
            <AlertCircle className="w-4 h-4" />
            Erreurs
          </button>
        </div>

        {/* Extended Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-700 grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Type d'action</label>
              <select
                value={actionTypeFilter}
                onChange={(e) => setActionTypeFilter(e.target.value)}
                className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:border-brand-green focus:outline-none"
              >
                <option value="">Tous</option>
                <option value="page_view">Vue de page</option>
                <option value="click">Clic</option>
                <option value="button_click">Clic bouton</option>
                <option value="scan_start">Démarrage scan</option>
                <option value="search">Recherche</option>
                <option value="api_call">Appel API</option>
                <option value="error">Erreur</option>
              </select>
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">Categorie</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:border-brand-green focus:outline-none"
              >
                <option value="">Toutes</option>
                <option value="navigation">Navigation</option>
                <option value="scan">Scan</option>
                <option value="exploitation">Exploitation</option>
                <option value="settings">Settings</option>
                <option value="auth">Auth</option>
                <option value="api">API</option>
              </select>
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">Page</label>
              <input
                type="text"
                placeholder="/dashboard, /scans..."
                value={pageFilter}
                onChange={(e) => setPageFilter(e.target.value)}
                className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm placeholder-gray-400 focus:border-brand-green focus:outline-none"
              />
            </div>
          </div>
        )}
      </div>

      {/* Actions Table */}
      <div className="bg-brand-black-light rounded-lg border border-gray-700 overflow-hidden">
        {/* Table Header */}
        <div className="grid grid-cols-12 gap-4 p-4 bg-brand-black border-b border-gray-700 text-xs font-medium text-gray-500 uppercase">
          <div className="col-span-2">Date/Heure</div>
          <div className="col-span-2">Action</div>
          <div className="col-span-3">Page / Ressource</div>
          <div className="col-span-2">Utilisateur</div>
          <div className="col-span-2">Client</div>
          <div className="col-span-1 text-center">Actions</div>
        </div>

        {/* Table Body */}
        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-brand-green mb-3" />
            <p className="text-gray-400">Chargement des logs...</p>
          </div>
        ) : actions.length === 0 ? (
          <div className="p-12 text-center">
            <Activity className="w-12 h-12 mx-auto text-gray-600 mb-3" />
            <p className="text-gray-400">Aucun log trouvé</p>
            <p className="text-gray-400 mt-1">Modifiez vos filtres ou attendez plus d'activite</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-800">
            {actions.map((action) => {
              const colors = getCategoryColors(action.action_category);
              const Icon = getActionIcon(action.action_type);

              return (
                <div
                  key={action.id}
                  className="grid grid-cols-12 gap-4 p-4 hover:bg-brand-black/50 transition-colors"
                >
                  {/* Date */}
                  <div className="col-span-2 text-sm">
                    <div className="text-white">{formatDate(action.created_at)?.split(' ')[0]}</div>
                    <div className="text-gray-500 text-xs">{formatDate(action.created_at)?.split(' ')[1]}</div>
                  </div>

                  {/* Action */}
                  <div className="col-span-2">
                    <div className="flex items-center gap-2">
                      <div className={`p-1.5 rounded ${colors.bg}`}>
                        <Icon className={`w-3 h-3 ${colors.text}`} />
                      </div>
                      <div>
                        <div className="text-white text-sm">{action.action_type}</div>
                        {action.action_category && (
                          <div className={`text-xs ${colors.text}`}>{action.action_category}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Page / Resource */}
                  <div className="col-span-3">
                    {action.page && (
                      <div className="text-white text-sm truncate">{action.page}</div>
                    )}
                    {action.page_title && (
                      <div className="text-gray-500 text-xs truncate">{action.page_title}</div>
                    )}
                    {action.resource_type && (
                      <div className="text-xs text-gray-500">
                        {action.resource_type}: {action.resource_name || action.resource_id}
                      </div>
                    )}
                    {action.error && (
                      <div className="text-xs text-red-400 flex items-center gap-1 mt-1">
                        <AlertCircle className="w-3 h-3" />
                        Erreur
                      </div>
                    )}
                  </div>

                  {/* User */}
                  <div className="col-span-2">
                    <div className="text-white text-sm truncate">
                      {action.user_id?.substring(0, 8) || 'Anonyme'}...
                    </div>
                    <div className="text-gray-500 text-xs">{action.ip_address || '-'}</div>
                  </div>

                  {/* Client */}
                  <div className="col-span-2">
                    <div className="text-white text-sm">{action.browser || '-'}</div>
                    <div className="text-gray-500 text-xs">{action.os || '-'} / {action.device_type || '-'}</div>
                  </div>

                  {/* Actions */}
                  <div className="col-span-1 text-center">
                    <button
                      onClick={() => setSelectedAction(action)}
                      className="p-2 text-gray-400 hover:text-brand-green rounded-lg hover:bg-brand-black"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {!loading && actions.length > 0 && (
          <div className="p-4 border-t border-gray-700 flex items-center justify-between">
            <div className="text-sm text-gray-400">
              Page {page} sur {totalPages} ({total} resultats)
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="p-2 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="px-3 py-1 bg-brand-black rounded text-white">
                {page}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="p-2 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Action Detail Modal */}
      <ActionDetailModal />
    </div>
  );
};

export default TrackingLogs;
